import React, {
  useCallback,
  useState,
  useEffect,
  ReactElement,
  MutableRefObject,
  useMemo,
} from 'react';
import { FormattedMessage, formatTranslation, TranslationKey } from 'intl';
import { CommonTranslationKeys, FileTranslationKeys, IssueTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import styles from './styles';
import { FileType, GU } from 'components/common/graphicUploader/types';
import { ButtonContained, ButtonText } from 'components/general';
import { ChangeEvent } from './types';
import { MAX_FILE_DESCRIPTION_LENGTH } from '../graphicUploader/helpers';
import useIsMobile from '../../../hooks/useIsMobile';
import { MemoSimpleTextField } from '../simpleInputFields/textField';
import {
  ErrorPresentation,
  stringMaxValidation,
} from 'helpers/validators';
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import cn from 'classnames';
import CloseIcon from '@mui/icons-material/Close';
import { useEditDescriptionForm } from '../../dataCreationForms/withEditDescriptionForm';
import { GalleryState, slideSelector } from './galleryStateStore';
import { Store, useCreateStore } from 'hooks/createStore';
import ForbiddenTooltip from 'components/general/ForbiddenTooltip/ForbiddenTooltip';
import { MainImage } from '../mainImage';
import { isSupportedAsMainImage } from 'shared/domain/document/isSupportedAsMainImage';
import { makeStyles } from '@mui/styles';

interface Props {
  image: FileType;
  id: number;
  edit?: GU['editFile'];
  close: () => void;
  mainImageHandlerRef: MutableRefObject<MainImage> | undefined;
}

function descriptionValidation(
  description: string | undefined
): ErrorPresentation {
  return stringMaxValidation(MAX_FILE_DESCRIPTION_LENGTH, description);
}

const EditDescription: React.FC<Props> = ({
  image,
  id,
  edit,
  close,
  mainImageHandlerRef,
}) => {
  const [description, setDescription] = useState(
    image.description ?? undefined
  );
  const checkedStore = useCreateStore(
    mainImageHandlerRef
      ? mainImageHandlerRef.current.getMainImageStore().get() === image._id
      : false
  );

  const isMobile = useIsMobile();
  const cs = styles({ isMobile });
  const intl = useIntl();

  const CANCEL = formatTranslation(intl, CommonTranslationKeys.cancel,);
  const SAVE = formatTranslation(intl, CommonTranslationKeys.save,);

  const error = useMemo<ErrorPresentation>(() => descriptionValidation(description), [description]);

  const handleSave = useCallback((): void => {
    edit?.(id, 'description', description);
    if (mainImageHandlerRef) {
      mainImageHandlerRef.current.saveMainImage(
        checkedStore.get(),
        image._id
      );
    }
    close();
  }, [
    edit,
    close,
    description,
    id,
    image._id,
    mainImageHandlerRef,
    checkedStore,
  ]);

  const handleChange = useCallback((e: ChangeEvent): void => {
    setDescription(e.target.value);
  }, []);

  const handleClose = useCallback(() => {
    close();
  }, [close]);

  const saveButtonDisabled = error !== undefined;

  return (
    <div>
      <div className={cs.editDescriptionRoot}>
        <MemoSimpleTextField
          handleChange={handleChange}
          labelId={FileTranslationKeys.fileInputDescription}
          fieldName='file-description'
          error={error}
          value={description}
          dense={false}
          reserveSpaceForHelperText={false}
        />
        <MainImageComponent
          mainImageHandlerRef={mainImageHandlerRef}
          image={image}
          checkedStore={checkedStore}
        />
      </div>
      <Divider style={{ marginTop: '16px' }} />
      <div
        className={cn(cs.buttonsContainer)}
        style={{
          padding: '16px 24px',
        }}
      >
        <ButtonText onClick={handleClose}>{CANCEL}</ButtonText>
        <ButtonContained
          onClick={handleSave}
          disabled={saveButtonDisabled}
        >
          {SAVE}
        </ButtonContained>
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  root: {
    '& .MuiDialog-paper': {
      margin: 16,
      width: '100%',
    },
  },
  dialogContent: {
    padding: 0,
  },
  closeIcon: {
    opacity: 0.54,
    cursor: 'pointer',
  },
  dialogTitle: {
    padding: '16px',
    '& .MuiTypography-root': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      height: '18px',
    },
  },
});

export function EditDescriptionDialog({
  editFile,
  useGalleryState,
  mainImageHandlerRef,
}: {
  editFile: GU['editFile'];
  useGalleryState: <T>(
    selector: (store: GalleryState) => T
  ) => [T, (value: GalleryState) => void];
  mainImageHandlerRef: MutableRefObject<MainImage> | undefined;
}): ReactElement {
  const { file, idx, setIndex, closeDialog, isOpen } =
    useEditDescriptionForm();
  const [slide] = useGalleryState(slideSelector);
  const classes = useStyles();

  // The dialog edits the gallery's current slide, which lives in the gallery store.
  useEffect(() => {
    setIndex(slide);
  }, [slide, setIndex]);

  return (
    <Dialog
      className={classes.root}
      open={isOpen}
      keepMounted={false}
      onClose={closeDialog}
    >
      <DialogTitle className={classes.dialogTitle}>
        <FormattedMessage id={IssueTranslationKeys.previewDescriptionEditDialogTitle}
        />
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.dialogContent}>
        <EditDescription
          image={file}
          id={idx}
          edit={editFile}
          close={closeDialog}
          mainImageHandlerRef={mainImageHandlerRef}
        />
      </DialogContent>
    </Dialog>
  );
}

function MainImageComponent({
  mainImageHandlerRef,
  image,
  checkedStore,
}: {
  mainImageHandlerRef: MutableRefObject<MainImage> | undefined;
  image: FileType;
  checkedStore: Store<boolean>;
}): ReactElement | null {
  return mainImageHandlerRef ? (
    <MainImageCheckbox
      key={`${mainImageHandlerRef.current.getMainImageStore().get()},${
        image._id
      }`}
      mainImageHandlerRef={mainImageHandlerRef}
      image={image}
      checkedStore={checkedStore}
    />
  ) : null;
}

function MainImageCheckbox({
  mainImageHandlerRef,
  image,
  checkedStore,
}: {
  mainImageHandlerRef: MutableRefObject<MainImage>;
  image: FileType;
  checkedStore: Store<boolean>;
}): ReactElement {
  const intl = useIntl();

  const [checked, setChecked] = useState(
    Boolean(image._id) &&
      mainImageHandlerRef.current.getMainImageStore().get() === image._id
  );

  useEffect(() => {
    if (!image._id) {
      return;
    }
    const action = (): void =>
      setChecked(
        mainImageHandlerRef.current.getMainImageStore().get() === image._id
      );
    action();
    return mainImageHandlerRef.current
      .getMainImageStore()
      .subscribe(action);
  }, [image._id, mainImageHandlerRef]);

  useEffect(() => {
    checkedStore.set(checked);
  }, [checkedStore, checked]);

  // @ts-expect-error Complex union type inference
  const disabledReason = useMemo<TranslationKey | ''>(() => {
    const extension = image.data?.extension;
    if (!extension || !isSupportedAsMainImage(extension)) {
      return FileTranslationKeys.fileUploadErrorFileTypeNotSupported;
    }
    if (!image._id) return FileTranslationKeys.mainImageDisabledImageNotUploadedYet;
    return '';
  }, [image]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
      }}
    >
      <ForbiddenTooltip
        visibleWhen={Boolean(disabledReason)}
        reason={disabledReason}
      >
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                color='primary'
                checked={checked}
                onChange={(_, isChecked) => {
                  setChecked(isChecked);
                }}
                disabled={Boolean(disabledReason)}
              />
            }
            name='use as main'
            label={formatTranslation(intl, FileTranslationKeys.useAsMainImage)}
          />
        </FormGroup>
      </ForbiddenTooltip>
    </Box>
  );
}
